import { z } from 'zod';
import { JsonValueZod } from './json.value.zod';

// `bind` is a plain dot-path string. Two namespaces are resolved at runtime
// by the surface engine — no structural schema difference between them:
//   - default: resolves against the form model (`customer.type`, `priority`).
//   - `$ctx.` prefix: resolves against a definition-supplied session-context
//     record instead of the model (rights, domain type, feature flags), e.g.
//     `{ op: 'eq', bind: '$ctx.rights.crm.manageSites', value: true }`.
//     The prefix is a runtime convention (see runtime/rule.condition.evaluator
//     wiring); missing paths resolve to undefined and honour the same operator
//     semantics as any absent bind. Only condition leaves learn this namespace
//     — rule targets and validators do not.
// `eqBind` / `neBind` compare two binds to each other instead of a bind to a
// literal `value`. `other` is the second bind path; it resolves through the SAME
// reader as `bind` (so the `$ctx.` session-context prefix is legal on either
// side). These enable cross-field conditions like "billTo.company._id !== _id"
// (the customer form's useOtherBillTo toggle). The literal `value` is ignored
// for these two ops — the comparison is bind-to-bind.
// `changed` is true once its bind changes in the current rule transaction. It is
// false on initialization unless an earlier rule in that transaction writes it.
const RuleConditionLeafZod = z.discriminatedUnion('op', [
  z.object({ op: z.literal('changed'), bind: z.string().min(1) }).strict(),
  z.object({ op: z.literal('eq'), bind: z.string().min(1), value: JsonValueZod }).strict(),
  z.object({ op: z.literal('ne'), bind: z.string().min(1), value: JsonValueZod }).strict(),
  z.object({ op: z.literal('in'), bind: z.string().min(1), value: z.array(JsonValueZod) }).strict(),
  z.object({ op: z.literal('notIn'), bind: z.string().min(1), value: z.array(JsonValueZod) }).strict(),
  z.object({ op: z.literal('gt'), bind: z.string().min(1), value: z.number() }).strict(),
  z.object({ op: z.literal('gte'), bind: z.string().min(1), value: z.number() }).strict(),
  z.object({ op: z.literal('lt'), bind: z.string().min(1), value: z.number() }).strict(),
  z.object({ op: z.literal('lte'), bind: z.string().min(1), value: z.number() }).strict(),
  z.object({ op: z.literal('isEmpty'), bind: z.string().min(1) }).strict(),
  z.object({ op: z.literal('isNotEmpty'), bind: z.string().min(1) }).strict(),
  z.object({
    op: z.enum(['eqBind', 'neBind']),
    bind: z.string().min(1),
    other: z.string().min(1),
  }).strict(),
]);

export type RuleConditionLeaf = z.infer<typeof RuleConditionLeafZod>;

export type RuleCondition =
  | RuleConditionLeaf
  | { op: 'and'; conditions: RuleCondition[] }
  | { op: 'or'; conditions: RuleCondition[] }
  | { op: 'not'; condition: RuleCondition };

export const RuleConditionZod: z.ZodType<RuleCondition> = z.lazy(() => z.union([
  RuleConditionLeafZod,
  z.object({ op: z.literal('and'), conditions: z.array(RuleConditionZod).min(1) }).strict(),
  z.object({ op: z.literal('or'), conditions: z.array(RuleConditionZod).min(1) }).strict(),
  z.object({ op: z.literal('not'), condition: RuleConditionZod }).strict(),
]));
